import React from 'react';
import { TradingSession } from '../types';

interface TradingSessionListProps {
  sessions: TradingSession[];
  isLoading: boolean;
  error: string | null;
  onSessionSelect: (sessionId: string) => void;
  onClose?: () => void;
  isLimited?: boolean;
  maxAllowed?: number;
}

export const TradingSessionList: React.FC<TradingSessionListProps> = ({
  sessions,
  isLoading,
  error,
  onSessionSelect,
  onClose,
  isLimited = false,
  maxAllowed = 0,
}) => {
  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner">⏳</div>
        <p>거래 내역을 불러오는 중...</p>
      </div>
    );
  }

  // 오류가 있고 세션이 없을 때만 오류 표시
  // 세션이 있으면 오류를 무시하고 세션 목록 표시
  if (error && sessions.length === 0) {
    return (
      <div className="error-state">
        <div className="error-icon">❌</div>
        <h3>오류가 발생했습니다</h3>
        <p>{error}</p>
      </div>
    );
  }

  // 세션이 없으면 (오류가 아니거나 오류가 있어도) 빈 상태 표시
  if (sessions.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">📊</div>
        <h3>아직 거래 내역이 없습니다</h3>
        <p>게임을 플레이하면 거래 내역이 여기에 표시됩니다.</p>
      </div>
    );
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatProfit = (profitRate: number) => {
    const sign = profitRate >= 0 ? '+' : '';
    const color = profitRate >= 0 ? '#22c55e' : '#ef4444';
    return (
      <span style={{ color }}>
        {sign}
        {profitRate.toFixed(2)}%
      </span>
    );
  };

  return (
    <div
      className="trading-session-list"
      style={{
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
      }}
    >
      {/* 제한 안내 메시지 */}
      {isLimited && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '1rem',
            borderRadius: '8px',
            background:
              'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <div style={{ fontSize: '2rem' }}>🔒</div>
          <div style={{ flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: '0.95rem',
                color: 'var(--text-primary)',
              }}
            >
              현재 버전은 {maxAllowed}개까지 조회가능합니다
            </p>
          </div>
        </div>
      )}

      <div className="session-list-header" style={{ padding: 0 }}>
        <h3>거래 세션 목록</h3>
        <p>
          {sessions.length}개의 거래 세션
          {isLimited && maxAllowed > 0 && ` (최대 ${maxAllowed}개 표시)`}
        </p>
      </div>

      <div
        className="session-table-container"
        style={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}
      >
        <table className="session-table">
          <thead>
            <tr>
              <th>날짜</th>
              <th>종목</th>
              <th>거래 횟수</th>
              <th>시작 잔고</th>
              <th>종료 잔고</th>
              <th>수익률</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map(session => (
              <tr
                key={session.id}
                className="session-table-row"
                onClick={() => onSessionSelect(session.id)}
              >
                <td>{formatDate(session.createDate || session.startTime)}</td>
                <td>
                  {session.stockName || session.stockCode || 'N/A'}
                  {session.stockName && session.stockCode && ` (${session.stockCode})`}
                </td>
                <td>{session.trades.length}회</td>
                <td>₩{session.initialBalance.toLocaleString()}</td>
                <td>₩{session.finalBalance.toLocaleString()}</td>
                <td>{formatProfit(session.profitRate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
